import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-alert.css?inline';

/**
 * `<pk-alert>` — inline callout for status messages.
 * See `docs/components/pk-alert.md`.
 *
 * Slots:
 *   - default — message content
 *
 * Attributes:
 *   - `kind` — `error` (default) | `info` | `success` | `warn`
 *
 * Accessibility:
 *   - `error` and `warn` kinds set `role="alert"` on the container so
 *     screen readers announce the message immediately.
 *   - `info` and `success` have no ARIA role (they are not urgent).
 */
export class PkAlert extends HTMLElement {
    static readonly tagName = 'pk-alert';

    static get observedAttributes(): string[] {
        return ['kind'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private template(): TemplateResult {
        const kind = this.getAttribute('kind') ?? 'error';
        const isUrgent = kind === 'error' || kind === 'warn';

        return html`
            <style>${styles}</style>
            <div
                class="alert alert--${kind}"
                role="${isUrgent ? 'alert' : ''}"
            >
                <slot></slot>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkAlert.tagName)) {
    customElements.define(PkAlert.tagName, PkAlert);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-alert': PkAlert;
    }
}
